import {ID} from '@wandb/weave/core/util/id';
import React, {useRef} from 'react';
import {twMerge} from 'tailwind-merge';

export type TemplateSection = {
  title: string;
  rows: {
    label: string;
    currDisplayValue: string | undefined;
    newDisplayValue?: string;
  }[];
};

export type WorkspaceTemplateDisplayValuesProps = {
  /**
   * templateCategory represents the primary template classification (e.g. "Line plots")
   */
  templateSections: TemplateSection[];
  /**
   * templateSections contains groups of settings within that category
   */
  templateCategory?: string;
};

export const WorkspaceTemplateDisplayValues = ({
  templateSections,
  templateCategory,
}: WorkspaceTemplateDisplayValuesProps) => {
  // Generate stable and unique IDs
  const ref = useRef({
    templateCategoryId: 'template-category-' + ID(),
  });

  return (
    <section
      className="mt-8 rounded bg-moon-300/[.24] p-16"
      aria-labelledby={ref.current.templateCategoryId}>
      {templateCategory && (
        <h5
          id={ref.current.templateCategoryId}
          className="mb-4 text-sm font-semibold uppercase text-moon-800">
          {templateCategory}
        </h5>
      )}
      <div className="flex w-full flex-col gap-8 text-sm text-moon-800">
        {templateSections.map((section, index) => {
          const sectionKey = `${ref.current.templateCategoryId}-${index}`;
          const sectionTitleId = `section-title-${sectionKey}`;

          return (
            <section key={sectionKey} aria-labelledby={sectionTitleId}>
              <h6 id={sectionTitleId}>{section.title}</h6>
              <dl role="list">
                {section.rows
                  .filter(
                    row =>
                      row.currDisplayValue != null ||
                      row.newDisplayValue != null
                  )
                  .map((row, i) => {
                    return (
                      <DisplayRow
                        key={`row-${i}-${sectionKey}`}
                        label={row.label}
                        currDisplayValue={row.currDisplayValue}
                        newDisplayValue={row.newDisplayValue}
                      />
                    );
                  })}
              </dl>
            </section>
          );
        })}
      </div>
    </section>
  );
};

const DisplayRow = ({
  label,
  currDisplayValue,
  newDisplayValue,
}: {
  label: string;
  currDisplayValue: string | undefined;
  newDisplayValue: string | undefined;
}) => {
  // Generate stable and unique IDs
  const ref = useRef({
    labelId: 'description-label-' + ID(),
  });

  const isValueModified =
    newDisplayValue != null && newDisplayValue !== currDisplayValue;

  return (
    <div className="flex text-moon-500" role="listitem">
      <dt className="w-1/3" id={ref.current.labelId}>
        {label}
      </dt>
      <span className="w-16">:</span>
      <dd className="m-0" aria-labelledby={ref.current.labelId}>
        {currDisplayValue && (
          <span
            className={twMerge(isValueModified && 'line-through')}
            aria-label="Existing template value">
            {currDisplayValue}
          </span>
        )}
        {isValueModified && (
          <span
            className={twMerge(currDisplayValue != null && 'ml-4')}
            aria-label="Updated template value">
            {newDisplayValue}
          </span>
        )}
      </dd>
    </div>
  );
};
